import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    ReactNode,
} from "react";

type UserSetting = "auto" | "light" | "dark";
type Theme = "light" | "dark";

interface ThemeContextProps {
    theme: Theme;
    userSetting: UserSetting;
    setUserSetting: (setting: UserSetting) => void;
    cycleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({
    children,
}) => {
    const [userSetting, setUserSetting] = useState<UserSetting>(() => {
        const stored = localStorage.getItem("userSetting");
        if (stored === "light" || stored === "dark" || stored === "auto") {
            return stored;
        }
        return "auto";
    });

    const [systemTheme, setSystemTheme] = useState<Theme>(() => {
        const prefersDark = window.matchMedia(
            "(prefers-color-scheme: dark)"
        ).matches;
        return prefersDark ? "dark" : "light";
    });

    useEffect(() => {
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        function handleSystemChange(e: MediaQueryListEvent) {
            setSystemTheme(e.matches ? "dark" : "light");
        }
        // Ensure systemTheme is correct on mount
        setSystemTheme(mediaQuery.matches ? "dark" : "light");
        mediaQuery.addEventListener("change", handleSystemChange);
        return () => {
            mediaQuery.removeEventListener("change", handleSystemChange);
        };
    }, []);

    // The actual theme in use
    const theme: Theme = userSetting === "auto" ? systemTheme : userSetting;

    useEffect(() => {
        localStorage.setItem("userSetting", userSetting);
    }, [userSetting]);

    // Instead of data-theme, add/remove the .dark class for Tailwind 'class' mode
    useEffect(() => {
        if (theme === "dark") {
            document.documentElement.classList.add("dark");
        } else {
            document.documentElement.classList.remove("dark");
        }
    }, [theme]);

    function cycleTheme() {
        setUserSetting((prev) => {
            if (prev === "auto") return "light";
            if (prev === "light") return "dark";
            return "auto";
        });
    }

    return (
        <ThemeContext.Provider
            value={{ theme, userSetting, setUserSetting, cycleTheme }}
        >
            {children}
        </ThemeContext.Provider>
    );
};

export function useTheme(): ThemeContextProps {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
}
